import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Container,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import { Button } from "../components/Buttons";
import { getMaintenanceStatus } from "../api";

const to12h = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (Number.isNaN(h)) return hhmm;
  const suffix = h < 12 ? "AM" : "PM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
};

/**
 * Shown while the admin-configured downtime window is active. Re-checks every
 * 60s and sends the user back to sign-in the moment the site reopens.
 */
export default function Maintenance() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = async ({ redirectWhenUp } = {}) => {
    setChecking(true);
    try {
      const { data } = await getMaintenanceStatus();
      setStatus(data);
      if (!data.down && redirectWhenUp) navigate("/signin");
    } catch {
      /* Backend unreachable — leave the notice up. */
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    check();
    const t = setInterval(() => check({ redirectWhenUp: true }), 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #14305a 0%, #2c5f9e 50%, #3b6fb0 100%)",
      }}
    >
      <Container maxWidth="xs">
        <Card sx={{ p: 4, borderRadius: 4, textAlign: "center" }}>
          <BuildRoundedIcon sx={{ fontSize: 56, color: "primary.main", mb: 1 }} />
          <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
            We'll be right back
          </Typography>
          <Typography color="text.secondary" fontSize={14} sx={{ mb: 2 }}>
            {status?.message || "The site is temporarily down for maintenance."}
          </Typography>
          {status?.end && (
            <Typography fontSize={14} sx={{ mb: 3 }}>
              Service resumes at <b>{to12h(status.end)} IST</b>.
            </Typography>
          )}
          <Stack alignItems="center" gap={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => check({ redirectWhenUp: true })}
              disabled={checking}
            >
              {checking ? <CircularProgress size={20} color="inherit" /> : "Check again"}
            </Button>
            <Typography fontSize={12} color="text.secondary">
              This page checks automatically every minute.
            </Typography>
          </Stack>
        </Card>
      </Container>
    </Box>
  );
}
